import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Zap, Clock, Loader2, Plus, Trash2, Lock, ArrowRight, ArrowLeft, Sparkles, Star, Navigation, ShieldCheck, Radar, Activity, Check, Crown, Heart, CheckCircle2, Users } from "lucide-react";
import { api, apiError, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PlantaoRadar from "@/components/express/PlantaoRadar";
import MapPlantao from "@/components/express/MapPlantao";
import LocationPicker, { pointToAddress } from "@/components/express/LocationPicker";
import { MapLegend, MusicianList } from "@/components/express/MapExtras";
import { LEGEND_PRE, LEGEND_LIVE } from "@/components/express/mapUtils";
import LiveSearch from "@/components/express/LiveSearch";
import { useLiveRefresh } from "@/lib/expressSocket";

const INSTRUMENTS = ["Percussão", "Cavaco", "Violão", "Baixo", "Teclado", "Guitarra", "Voz", "Bateria", "Pandeiro", "Saxofone", "Trompete", "Sanfona"];
const inp = "bg-[#09090B] border-white/15 rounded-xl h-11";
const todayISO = () => new Date().toLocaleDateString("en-CA");
const tomorrowISO = () => new Date(Date.now() + 86400000).toLocaleDateString("en-CA");
const prettyToday = () => new Date().toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
const card = "rounded-2xl border border-white/10 bg-[#121217] p-5";

export default function ExpressEvent() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { user } = useAuth();
  const tab = sp.get("tab") === "andamento" ? "andamento" : "criar";
  const setTab = (t) => setSp(t === "andamento" ? { tab: "andamento" } : {});
  const isCreatorCtx = user?.user_type === "creator" || user?.user_type === "both";
  const isAdmin = ["admin", "super_admin"].includes(user?.role);
  const hasExpress = isAdmin || (user?.entitlements || []).includes("express_oncall");

  if (!isCreatorCtx) return (
    <div className="max-w-lg mx-auto text-center py-16" data-testid="express-blocked">
      <Zap className="h-10 w-10 text-[#FF4D00] mx-auto mb-3" />
      <h1 className="font-head text-xl font-bold">Em cima da Hora é para Criadores</h1>
      <p className="text-muted-foreground mt-2 text-sm">Freelancers participam ativando o <b>Modo Plantão</b> para receber chamadas ⚡ compatíveis com agenda, instrumento, distância e cachê.</p>
      <Button onClick={() => navigate("/plantao")} className="mt-4 bg-[#FF4D00] hover:bg-[#E64500] rounded-xl">Ir para o Plantão</Button>
    </div>
  );

  if (!hasExpress) return (
    <div className="max-w-lg mx-auto text-center py-16" data-testid="express-upgrade">
      <Zap className="h-10 w-10 text-[#FF4D00] mx-auto mb-3" />
      <h1 className="font-head text-xl font-bold">Contratação instantânea dos planos superiores</h1>
      <p className="text-muted-foreground mt-2 text-sm">O <b>⚡ Em cima da Hora</b> está disponível nos planos <b>Pro+</b> e <b>Business</b>. Faça upgrade para encontrar e contratar músicos disponíveis agora, na sua região.</p>
      <Button onClick={() => navigate("/planos")} className="mt-4 bg-[#FF4D00] hover:bg-[#E64500] rounded-xl">Ver planos</Button>
    </div>
  );

  return (
    <div className={`${tab === "andamento" ? "max-w-6xl" : "max-w-2xl"} mx-auto space-y-5 pb-10`} data-testid="express-page">
      <Header />
      <div className="flex p-1 rounded-2xl border border-white/10 bg-[#0D0D11]" data-testid="express-tabs">
        {[["criar", "Criar chamada", Zap], ["andamento", "Em andamento", Activity]].map(([v, l, Icon]) => (
          <button key={v} data-testid={`express-tab-${v}`} onClick={() => setTab(v)} aria-pressed={tab === v}
            className={`flex-1 h-11 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8A3D] text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-all ${tab === v ? "bg-[#FF4D00] text-white shadow-[0_0_20px_rgba(255,79,0,0.3)]" : "text-muted-foreground hover:text-foreground"}`}>
            <Icon className="h-4 w-4" aria-hidden="true" />{l}
          </button>
        ))}
      </div>
      {tab === "criar" ? <CreateCall user={user} /> : <OngoingCalls user={user} />}
    </div>
  );
}

function Header() {
  const [usage, setUsage] = useState(null);
  useEffect(() => { api.get("/me/plan").then((r) => setUsage(r.data)).catch(() => {}); }, []);
  const planName = usage?.plan?.name || "";
  return (
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div>
        <h1 className="font-head text-3xl font-extrabold tracking-tight flex items-center gap-2.5"><Zap className="h-7 w-7 text-[#FF4D00]" />Em cima da Hora</h1>
        <p className="text-muted-foreground mt-1 text-sm">Encontre músicos disponíveis agora na sua região.</p>
      </div>
      {usage && (
        <div className="flex items-center gap-2 shrink-0" data-testid="express-plan-chips">
          {planName && <span className="text-xs px-3 py-1.5 rounded-full border border-white/10 bg-white/5 text-foreground font-medium">Plano {planName}</span>}
          <span className="text-xs px-3 py-1.5 rounded-full border border-[#FF4D00]/30 bg-[#FF4D00]/10 text-[#FF4D00] font-semibold" data-testid="express-plan-remaining">
            {usage.unlimited ? "Eventos ilimitados" : `${usage.remaining} eventos restantes`}
          </span>
        </div>
      )}
    </div>
  );
}

function Stepper({ step }) {
  const steps = [["Detalhes"], ["Instrumentos"], ["Revisar"]];
  return (
    <nav aria-label={`Progresso: etapa ${step} de 3`}>
      <ol className="flex items-center justify-between px-1" data-testid="express-stepper">
        {steps.map(([l], i) => {
          const n = i + 1, done = step > n, active = step === n;
          return (
            <li key={l} className="flex items-center flex-1 last:flex-none" aria-current={active ? "step" : undefined}>
              <div className="flex flex-col items-center gap-1">
                <div aria-hidden="true" className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${done ? "bg-emerald-500 text-zinc-950" : active ? "bg-[#FF4D00] text-white" : "bg-white/10 text-zinc-300"}`}>
                  {done ? <Check className="h-4 w-4" /> : n}
                </div>
                <span className={`text-xs ${active || done ? "text-foreground font-semibold" : "text-zinc-400"}`}>{l}<span className="sr-only">{done ? " (concluída)" : active ? " (etapa atual)" : ""}</span></span>
              </div>
              {i < 2 && <div aria-hidden="true" className={`flex-1 h-0.5 mx-1 -mt-4 ${step > n ? "bg-emerald-500" : "bg-white/10"}`} />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function CreateCall({ user }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [started, setStarted] = useState(false);
  const [when, setWhen] = useState("agora");
  const [xs, setXs] = useState(null);
  const [usage, setUsage] = useState(null);
  const [f, setF] = useState({ start_time: nowPlus(10).time, date: nowPlus(10).date, duration_h: 2, notes: "", type: "Particular", structure: [] });
  const [loc, setLoc] = useState({ address: "", lat: null, lng: null });
  const [pickMode, setPickMode] = useState(false);
  const rootRef = useRef(null);
  const [cache, setCache] = useState(300);
  const [roles, setRoles] = useState([{ instrument: "Percussão", quantity: 1 }]);
  const [musicians, setMusicians] = useState(null);
  const [loadingPrev, setLoadingPrev] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [sug, setSug] = useState(null);
  const [adding, setAdding] = useState(false);
  const [protect, setProtect] = useState(false);
  const [radius, setRadius] = useState(5);
  const [costPrev, setCostPrev] = useState(null);

  useEffect(() => { api.get("/me/plan").then((r) => setUsage(r.data)).catch(() => {}); api.get("/express/settings").then((r) => setXs(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (when === "outro") return;
    const p = nowPlus(when === "agora" ? 10 : 60);
    setF((x) => ({ ...x, start_time: p.time, date: p.date }));
  }, [when]);
  const setOtherTime = (t) => {
    const [h, m] = t.split(":").map(Number);
    const d = new Date(); d.setHours(h, m, 0, 0);
    if (d.getTime() < Date.now() - 15 * 60000) d.setDate(d.getDate() + 1);
    setF((x) => ({ ...x, start_time: t, date: d.toLocaleDateString("en-CA") }));
  };
  const minAdv = xs?.min_advance_hours || 24;
  const startMs = new Date(`${f.date}T${f.start_time}:00`).getTime();
  const timeErr = startMs - Date.now() > minAdv * 3600000 ? `Em cima da Hora é para inícios em até ${minAdv}h. Para depois disso, crie um evento planejado.` : "";
  const center = useMemo(() => (loc.lat != null ? { lat: loc.lat, lng: loc.lng } : user?.lat ? { lat: user.lat, lng: user.lng } : null), [loc.lat, loc.lng, user?.lat, user?.lng]);
  const onMapPick = useCallback((pt) => { setPickMode(false); pointToAddress(pt, loc.address, setLoc); }, [loc.address]);
  useEffect(() => { rootRef.current?.querySelector("[data-step-heading]")?.focus(); }, [step]);

  const endTime = useMemo(() => {
    const [h, m] = f.start_time.split(":").map(Number);
    const e = (h + Number(f.duration_h)) % 24;
    return `${String(e).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  }, [f.start_time, f.duration_h]);
  const wrapsMidnight = useMemo(() => {
    const [h] = f.start_time.split(":").map(Number);
    return (h + Number(f.duration_h)) >= 24;
  }, [f.start_time, f.duration_h]);

  useEffect(() => {
    const inst = roles[0]?.instrument;
    if (!inst) return;
    api.get("/cache-suggestion", { params: { instrument: inst } }).then((r) => setSug(r.data?.suggested || 0)).catch(() => {});
  }, [roles]);

  const setRole = (i, k, v) => setRoles((p) => p.map((r, j) => j === i ? { ...r, [k]: v } : r));
  const incInstrument = (inst) => setRoles((p) => { const ex = p.find((r) => r.instrument === inst); return ex ? p.map((r) => r.instrument === inst ? { ...r, quantity: r.quantity + 1 } : r) : [...p, { instrument: inst, quantity: 1 }]; });
  const decInstrument = (inst) => setRoles((p) => p.flatMap((r) => r.instrument !== inst ? [r] : (r.quantity > 1 ? [{ ...r, quantity: r.quantity - 1 }] : [])));
  const qtyOf = (inst) => roles.find((r) => r.instrument === inst)?.quantity || 0;
  const addRole = (inst) => { incInstrument(inst); setAdding(false); };
  const delRole = (i) => setRoles((p) => p.filter((_, j) => j !== i));

  const loadRadar = useCallback(async () => {
    const instrs = [...new Set(roles.map((r) => r.instrument).filter(Boolean))];
    if (!instrs.length || !f.start_time) return;
    setLoadingPrev(true);
    try {
      const lists = await Promise.all(instrs.map((inst) =>
        api.get("/oncall/search", { params: { instrument: inst, date: f.date, start_time: f.start_time, end_time: endTime, eta_feasible: true, include_available: true, ...(center ? { lat: center.lat, lng: center.lng } : {}) } })
          .then((r) => r.data).catch(() => [])));
      const byId = new Map();
      lists.forEach((l) => (l || []).forEach((m) => { const ex = byId.get(m.id); if (!ex || (m.match || 0) > (ex.match || 0)) byId.set(m.id, m); }));
      setMusicians([...byId.values()].sort((a, b) => (b.match || 0) - (a.match || 0)));
    } catch { setMusicians([]); }
    finally { setLoadingPrev(false); }
  }, [roles, f.start_time, f.date, endTime, center]);
  useEffect(() => { const t = setTimeout(loadRadar, 450); return () => clearTimeout(t); }, [loadRadar]);

  const bands = useMemo(() => {
    const ms = musicians || [];
    const mk = (max, min) => {
      const inb = ms.filter((m) => m.distance_km != null && m.distance_km <= max && (min == null || m.distance_km > min));
      const etas = inb.map((m) => m.eta_min).filter((x) => x != null);
      return { n: inb.length, etaMin: etas.length ? Math.min(...etas) : null, etaMax: etas.length ? Math.max(...etas) : null };
    };
    const b5 = mk(5, null), b10 = mk(10, 5), b20 = mk(20, 10);
    return { b5: b5.n, b10: b10.n + b5.n, b20: b20.n + b10.n + b5.n, raw: { b5, b10, b20 } };
  }, [musicians]);

  const etaTxt = (b) => b.etaMin == null ? "—" : (b.etaMin === b.etaMax ? `ETA ${b.etaMin} min` : `ETA ${b.etaMin}–${b.etaMax} min`);
  const totalSlots = roles.reduce((a, r) => a + (parseInt(r.quantity) || 0), 0);
  const canCreate = !usage || usage.can_create;
  const topMatches = (musicians || []).slice(0, 4);
  const inRadius = (musicians || []).filter((m) => m.distance_km != null && m.distance_km <= radius);
  const nearby = inRadius.length;
  const onDuty = inRadius.filter((m) => m.oncall_status === "ativo").length;
  const mapSummary = loadingPrev ? "Atualizando músicos próximos…" : `${nearby} músico${nearby === 1 ? "" : "s"} no raio de ${radius} km, ${onDuty} em Plantão.`;

  useEffect(() => {
    if (step !== 3) return;
    api.get("/express/cost-preview", { params: { cache: parseFloat(cache) || 0, slots: totalSlots } })
      .then((r) => setCostPrev(r.data)).catch(() => setCostPrev(null));
  }, [step, cache, totalSlots]);

  const publish = async () => {
    if (!loc.address.trim()) { setStep(2); return toast.error("Informe o local."); }
    const clean = roles.filter((r) => r.instrument && (parseInt(r.quantity) || 0) >= 1)
      .map((r) => ({ instrument: r.instrument, quantity: parseInt(r.quantity) || 1, cache: parseFloat(cache) || 0, min_level: null }));
    if (!clean.length) return toast.error("Adicione ao menos um instrumento.");
    setPublishing(true);
    try {
      const names = clean.map((r) => r.instrument).join(" + ");
      const endDate = wrapsMidnight ? new Date(new Date(`${f.date}T12:00:00`).getTime() + 86400000).toLocaleDateString("en-CA") : f.date;
      const payload = {
        name: `⚡ Em cima da Hora — ${names}`, origin: "EM_CIMA_DA_HORA", type: f.type,
        date: f.date, end_date: endDate, start_time: f.start_time, end_time: endTime,
        address: loc.address.trim(), lat: loc.lat, lng: loc.lng, notes: f.notes, roles: clean,
        requirements: f.structure.length ? `Estrutura fornecida: ${f.structure.join(", ")}.` : "",
        payment_mode: protect ? "protected" : "flexible",
        invite_deadline_min: 240, confirm_deadline_min: 30,
      };
      const { data } = await api.post("/events", payload);
      toast.success("⚡ Chamada publicada! Acompanhe a busca ao vivo no mapa.");
      navigate(`/em-cima-da-hora?tab=andamento&call=${data.id}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "plan_limit_reached") { toast.error("Limite de eventos atingido. Faça upgrade."); navigate("/eventos?tab=mine"); }
      else if (d && typeof d === "object" && d.code === "feature_locked") { toast.error(d.message || "Recurso indisponível no seu plano."); navigate("/planos"); }
      else toast.error(apiError(d));
    } finally { setPublishing(false); }
  };

  if (!started) return <EntryCards onStart={() => setStarted(true)} />;

  return (
    <div className="space-y-5" ref={rootRef}>
      <Stepper step={step} />

      {/* ETAPA 2 — Instrumentos + músicos no raio */}
      {step === 2 && (
        <div className="space-y-4" data-testid="express-step-instruments">
          <h2 data-step-heading tabIndex={-1} className="sr-only">Etapa 2: escolha os instrumentos e veja os músicos próximos</h2>
          <PreviewMap center={center} musicians={musicians || []} radius={radius} setRadius={setRadius} nearby={nearby} onDuty={onDuty} loading={loadingPrev} summary={mapSummary} hasLoc={center != null} />

          <div className={card}>
            <h3 id="express-inst-title" className="font-head text-lg font-bold mb-0.5">O que você precisa?</h3>
            <p className="text-xs text-muted-foreground mb-3">Use + e − para definir quantos músicos de cada instrumento.</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" role="group" aria-labelledby="express-inst-title">
              {[...new Set([...roles.map((r) => r.instrument), "Percussão", "Cavaco", "Violão", "Baixo"])].slice(0, 7).map((inst) => (
                <InstrumentCard key={inst} inst={inst} qty={qtyOf(inst)} onInc={() => incInstrument(inst)} onDec={() => decInstrument(inst)} />
              ))}
              {adding ? (
                <select autoFocus data-testid="express-add-select" aria-label="Escolher outro instrumento" onChange={(e) => e.target.value && addRole(e.target.value)} onBlur={() => setAdding(false)} defaultValue=""
                  className="h-full min-h-[96px] bg-[#09090B] border border-[#FF4D00]/40 rounded-xl px-2 text-sm">
                  <option value="" disabled>Escolher…</option>
                  {INSTRUMENTS.filter((x) => !roles.find((r) => r.instrument === x)).map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              ) : (
                <button type="button" onClick={() => setAdding(true)} data-testid="express-add-role"
                  className="min-h-[96px] rounded-xl border border-dashed border-white/25 flex flex-col items-center justify-center gap-1 text-zinc-300 hover:text-foreground hover:border-white/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8A3D]">
                  <Plus className="h-5 w-5" aria-hidden="true" /><span className="text-xs leading-tight text-center">Mais instrumentos</span>
                </button>
              )}
            </div>
            <div className="flex gap-2 mt-4">
              <Button data-testid="express-back-instruments" variant="outline" onClick={() => setStep(1)} className="h-12 rounded-2xl bg-transparent border-white/15 gap-1.5 px-5"><ArrowLeft className="h-4 w-4" aria-hidden="true" />Voltar</Button>
              <Button data-testid="express-next-instruments" onClick={() => { if (!totalSlots) return toast.error("Selecione ao menos 1 músico."); setStep(3); }}
                className="flex-1 h-12 rounded-2xl bg-gradient-to-r from-[#FF4D00] to-accent text-white font-bold gap-2">
                Avançar <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ETAPA 1 — Detalhes da chamada */}
      {step === 1 && (
        <div className="space-y-4" data-testid="express-step-details">
          <div className={card + " space-y-4"}>
            <h2 data-step-heading tabIndex={-1} className="font-head text-lg font-bold focus:outline-none">Informe a necessidade</h2>
            <LocationPicker value={loc} onChange={setLoc} pickMode={pickMode} setPickMode={setPickMode} />
            <MapPlantao center={center} musicians={musicians || []} radiusKm={radius} onPick={onMapPick} pickMode={pickMode} height={240}
              label="Mapa para definir o local do evento. Ative Marcar no mapa e clique para escolher o ponto, ou arraste o pino laranja." />
            <WhenPicker when={when} setWhen={setWhen} time={f.start_time} date={f.date} setTime={setOtherTime} err={timeErr} />
            <div className="grid sm:grid-cols-2 gap-3">
              <DurationStepper value={Number(f.duration_h)} onChange={(v) => setF({ ...f, duration_h: v })} />
              <div className="space-y-1.5">
                <Label htmlFor="express-type" className="text-zinc-300 text-sm">Tipo de evento</Label>
                <select id="express-type" data-testid="express-type" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className="w-full h-11 bg-[#09090B] border border-white/15 rounded-xl px-3 text-sm">
                  {["Particular", "Bar", "Show", "Casamento", "Corporativo", "Festival", "Culto"].map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="express-cache" className="text-zinc-300 text-sm">Cachê por músico (R$)</Label>
                <div className="relative"><span aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">R$</span>
                  <Input id="express-cache" data-testid="express-cache" inputMode="decimal" type="number" min="0" value={cache} onChange={(e) => setCache(e.target.value)} className={`${inp} pl-9`} /></div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="express-notes" className="text-zinc-300 text-sm">Observações (opcional)</Label>
                <Input id="express-notes" data-testid="express-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Ex.: traje preto / pagode" className={inp} />
              </div>
            </div>
            <fieldset>
              <legend className="text-zinc-300 text-sm mb-2">Estrutura fornecida</legend>
              <div className="flex flex-wrap gap-2">
                {["Som / PA", "Instrumentos no local", "Camarim", "Alimentação", "Estacionamento"].map((s) => {
                  const on = f.structure.includes(s);
                  return <button key={s} type="button" role="checkbox" aria-checked={on} data-testid={`express-structure-${s}`} onClick={() => setF({ ...f, structure: on ? f.structure.filter((x) => x !== s) : [...f.structure, s] })}
                    className={`min-h-[44px] px-3 rounded-xl border text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8A3D] ${on ? "border-[#FF4D00] bg-[#FF4D00]/15 text-[#FFB27A]" : "border-white/15 text-zinc-300"}`}>{on ? "✓ " : ""}{s}</button>;
                })}
              </div>
            </fieldset>
          </div>

          <div className={card}>
            <h3 id="express-radius-title" className="font-head font-bold">Raio de busca</h3>
            <p className="text-xs text-muted-foreground mb-3">O sistema amplia automaticamente se necessário.</p>
            <RadiusPicker radius={radius} setRadius={setRadius} labelledBy="express-radius-title" />
            <p className="text-xs text-muted-foreground mt-2" aria-live="polite">Busca inicial em até {radius} km do local. {mapSummary}</p>
          </div>

          <div className="flex gap-2">
            <Button data-testid="express-back-2" variant="outline" onClick={() => setStarted(false)} className="h-12 rounded-2xl bg-transparent border-white/15 gap-1.5 px-5"><ArrowLeft className="h-4 w-4" />Voltar</Button>
            <Button data-testid="express-next-details" onClick={() => { if (!loc.address.trim()) { document.getElementById("express-address")?.focus(); return toast.error("Informe o local do evento."); } if (timeErr) return toast.error(timeErr); setStep(2); }}
              className="flex-1 h-12 rounded-2xl bg-gradient-to-r from-[#FF4D00] to-accent text-white font-bold gap-2">Avançar <ArrowRight className="h-4 w-4" aria-hidden="true" /></Button>
          </div>
        </div>
      )}

      {/* ETAPA 3 — Radar + matches + cachê + resumo/publicação */}
      {step === 3 && (
        <div className="space-y-5" data-testid="express-step-3">
          <div className={card}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h2 data-step-heading tabIndex={-1} className="font-head font-bold flex items-center gap-2 focus:outline-none"><Radar className="h-5 w-5 text-[#FF4D00]" aria-hidden="true" />Radar do Plantão</h2>
                <p className="text-xs text-muted-foreground">Disponíveis agora e compatíveis com sua chamada.</p>
              </div>
              <span className="text-xs text-muted-foreground">20 km</span>
            </div>
            <div className="grid sm:grid-cols-[180px_1fr] gap-4 items-center">
              <PlantaoRadar b5={bands.raw.b5.n} b10={bands.raw.b10.n} b20={bands.raw.b20.n} />
              <div className="divide-y divide-white/5" data-testid="express-bands">
                {loadingPrev ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#FF4D00]" /></div> : [
                  { l: "Até 5 km", sub: etaTxt(bands.raw.b5), n: bands.b5 },
                  { l: "Até 10 km", sub: `${bands.raw.b10.n} nesta faixa · ${etaTxt(bands.raw.b10)}`, n: bands.b10 },
                  { l: "Até 20 km", sub: "Ampliação automática", n: bands.b20 },
                ].map((b, i) => (
                  <div key={i} className="flex items-center justify-between py-3" data-testid={`express-band-${i}`}>
                    <div><div className="text-sm font-medium">{b.l}</div><div className="text-xs text-muted-foreground">{b.sub}</div></div>
                    <div className="font-head font-bold text-base">{b.n} <span className="text-xs text-muted-foreground font-normal">músico{b.n === 1 ? "" : "s"}</span></div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {topMatches.length > 0 && (
            <div className={card}>
              <h3 className="font-head font-bold flex items-center gap-2"><Sparkles className="h-5 w-5 text-[#FF4D00]" />Melhores matches</h3>
              <p className="text-xs text-muted-foreground mb-3">Agenda + Plantão + ETA + Score + histórico.</p>
              <div className="space-y-2.5" data-testid="express-matches">
                {topMatches.map((m) => (
                  <div key={m.id} data-testid={`express-match-${m.id}`} className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#09090B] p-3">
                    <div className="h-10 w-10 rounded-full bg-[#1C1C24] overflow-hidden flex items-center justify-center font-bold shrink-0">
                      {m.photo ? <img src={m.photo} alt="" className="h-full w-full object-cover" /> : (m.stage_name?.[0] || "?")}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{m.stage_name || (m.instruments?.[0] || "Músico")}</div>
                      <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 mt-0.5 text-xs text-muted-foreground">
                        {m.rating?.avg > 0 && <span className="flex items-center gap-0.5"><Star className="h-3 w-3 text-amber-400 fill-amber-400" />{m.rating.avg}</span>}
                        {m.distance_km != null && <span>{m.distance_km} km</span>}
                        {m.eta_min != null && <span className="flex items-center gap-0.5 text-[#FF4D00]"><Navigation className="h-3 w-3" />ETA {m.eta_min} min</span>}
                        {m.oncall_status === "ativo" && <span className="text-emerald-400">Plantão ativo</span>}
                        {m.in_team && <span className="inline-flex items-center gap-0.5 text-rose-300"><Heart className="h-3 w-3 fill-rose-300" />Minha Equipe</span>}
                        {m.history_with_creator > 0 && <span className="inline-flex items-center gap-0.5"><Crown className="h-3 w-3" />já trabalhou com você</span>}
                      </div>
                    </div>
                    <span className="text-sm font-bold text-[#FF4D00] shrink-0">{m.match}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {sug > 0 && (
            <div className={card + " flex items-center justify-between gap-3"} data-testid="express-cache-smart">
              <div><h3 className="font-head font-bold flex items-center gap-2"><Sparkles className="h-5 w-5 text-accent" />Cachê Inteligente</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Para esta urgência, distância e horário, sugerimos <b className="text-foreground">{money(sug)}</b> por músico.</p></div>
              {Number(cache) !== sug && <Button data-testid="express-cache-use" variant="outline" onClick={() => setCache(sug)} className="rounded-xl bg-transparent border-accent/40 text-accent shrink-0">Usar {money(sug)}</Button>}
            </div>
          )}

          <div className="rounded-2xl border border-[#FF4D00]/30 bg-[#FF4D00]/[0.05] p-5 space-y-3" data-testid="express-summary">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-[#FF4D00] font-bold">Resumo da publicação</div>
                <div className="font-head font-bold flex items-center gap-2 mt-1"><Zap className="h-5 w-5 text-[#FF4D00]" />Em cima da Hora</div>
                <div className="text-sm text-foreground mt-1">{f.date === todayISO() ? "Hoje" : "Amanhã"} · {f.start_time}–{endTime} · {roles.map((r) => `${r.instrument} ×${r.quantity}`).join(" · ")}</div>
                <div className="text-sm text-muted-foreground">{loc.address} · {f.type}</div>
                <div className="text-sm text-muted-foreground">Cachê: <b className="text-foreground">{money(parseFloat(cache) || 0)}</b> por músico · {totalSlots} músico(s)</div>
              </div>
              {usage && !usage.unlimited && (
                <div className="text-right shrink-0" data-testid="express-plan-counter">
                  <div className="text-xs text-muted-foreground">Eventos do plano</div>
                  <div className="font-head text-xl font-bold">{usage.remaining} <span className="text-muted-foreground">→</span> {Math.max((usage.remaining || 0) - 1, 0)}</div>
                  <div className="text-xs text-muted-foreground">após publicar</div>
                </div>
              )}
            </div>
            <div className="text-xs text-muted-foreground rounded-xl bg-black/20 p-3">
              Esta chamada será contabilizada como 1 evento do seu plano. Consultar o Radar ou sair sem publicar não consome evento.
            </div>
            {costPrev && (
              <div className="rounded-xl bg-black/20 p-3 space-y-1 text-sm" data-testid="express-cost-breakdown">
                <div className="flex justify-between"><span className="text-muted-foreground">Cachês ({totalSlots} músico{totalSlots === 1 ? "" : "s"})</span><span>{money(costPrev.subtotal)}</span></div>
                {costPrev.platform_fee > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Taxa Mí Chamou</span><span>{money(costPrev.platform_fee)}</span></div>}
                {costPrev.express_surcharge > 0 && <div className="flex justify-between"><span className="text-[#FF4D00]">⚡ Acréscimo Em cima da Hora</span><span className="text-[#FF4D00]">{money(costPrev.express_surcharge)}</span></div>}
                {costPrev.gateway_fee > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Gateway (Pix)</span><span>{money(costPrev.gateway_fee)}</span></div>}
                <div className="flex justify-between font-bold border-t border-white/10 pt-1 mt-1"><span>Total ao pagar a banda</span><span data-testid="express-cost-total">{money(costPrev.total)}</span></div>
                <p className="text-xs text-muted-foreground">Cobrança só acontece ao pagar os músicos confirmados. Publicar consome 1 evento do plano.</p>
              </div>
            )}
            <button type="button" onClick={() => setProtect((v) => !v)} data-testid="express-protect-toggle" role="checkbox" aria-checked={protect}
              className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition-colors ${protect ? "border-emerald-500/40 bg-emerald-500/[0.07]" : "border-white/10 bg-black/20"}`}>
              <div className={`h-5 w-5 rounded-md border flex items-center justify-center shrink-0 ${protect ? "bg-emerald-500 border-emerald-500" : "border-white/30"}`}>{protect && <CheckCircle2 className="h-4 w-4 text-zinc-950" />}</div>
              <div className="min-w-0"><div className="text-sm font-medium flex items-center gap-1.5"><ShieldCheck className={`h-4 w-4 ${protect ? "text-emerald-400" : "text-muted-foreground"}`} />Usar Cachê Protegido <span className="text-xs text-muted-foreground font-normal">(opcional)</span></div>
                <div className="text-xs text-muted-foreground">Valor pago antecipadamente fica retido e só é repassado após check-in/check-out — recomendado para urgência.</div></div>
            </button>
            <div className="flex gap-2">
              <Button data-testid="express-back-3" variant="outline" onClick={() => setStep(2)} className="h-12 rounded-2xl bg-transparent border-white/15 gap-1.5 px-5"><ArrowLeft className="h-4 w-4" />Voltar</Button>
              {canCreate ? (
                <Button data-testid="express-publish-btn" onClick={publish} disabled={publishing}
                  className="flex-1 h-12 rounded-2xl bg-gradient-to-r from-[#FF4D00] to-accent text-white font-bold text-base gap-2">
                  {publishing ? <Loader2 className="h-5 w-5 animate-spin" /> : <Zap className="h-5 w-5" />}Criar chamada
                </Button>
              ) : (
                <Button data-testid="express-blocked-btn" onClick={() => navigate("/eventos?tab=mine")} variant="outline"
                  className="flex-1 h-12 rounded-2xl bg-transparent border-[#FF4D00]/40 text-[#FF4D00] gap-2"><Lock className="h-4 w-4" />Limite atingido — ver opções</Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function nowPlus(min) {
  const d = new Date(Date.now() + min * 60000);
  d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5, 0, 0);
  return { date: d.toLocaleDateString("en-CA"), time: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}` };
}

function EntryCards({ onStart }) {
  const navigate = useNavigate();
  const item = "w-full text-left rounded-2xl border p-5 flex items-center gap-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8A3D] min-h-[88px]";
  return (
    <div className="space-y-3" data-testid="express-entry">
      <button type="button" onClick={() => navigate("/eventos/novo")} data-testid="express-entry-planned" className={`${item} border-white/10 bg-[#121217] hover:border-white/25`}>
        <span aria-hidden="true" className="h-12 w-12 rounded-xl bg-white/5 flex items-center justify-center"><Clock className="h-6 w-6 text-zinc-300" /></span>
        <span className="flex-1"><span className="block font-head font-bold">Evento planejado</span><span className="block text-sm text-zinc-300">Crie com antecedência mínima de 12 a 24 horas.</span></span>
        <ArrowRight className="h-5 w-5 text-zinc-400" aria-hidden="true" />
      </button>
      <button type="button" onClick={onStart} data-testid="express-entry-urgent" className={`${item} border-[#FF4D00]/60 bg-[#FF4D00]/[0.08] hover:bg-[#FF4D00]/[0.14] shadow-[0_0_30px_rgba(255,77,0,0.12)]`}>
        <span aria-hidden="true" className="h-12 w-12 rounded-xl bg-[#FF4D00]/20 flex items-center justify-center"><Zap className="h-6 w-6 text-[#FF4D00]" /></span>
        <span className="flex-1"><span className="block font-head font-bold">Em cima da Hora</span><span className="block text-sm text-zinc-300">Para urgências e demandas imediatas.</span></span>
        <ArrowRight className="h-5 w-5 text-[#FF8A3D]" aria-hidden="true" />
      </button>
    </div>
  );
}

function WhenPicker({ when, setWhen, time, date, setTime, err }) {
  const opts = [["agora", "Agora"], ["1h", "Em até 1 hora"], ["outro", "Outro horário"]];
  return (
    <fieldset className="space-y-2">
      <legend className="text-zinc-300 text-sm mb-1">Quando precisa?</legend>
      <div role="radiogroup" aria-label="Quando precisa" className="grid grid-cols-3 gap-2">
        {opts.map(([k, l]) => (
          <button key={k} type="button" role="radio" aria-checked={when === k} onClick={() => setWhen(k)} data-testid={`express-when-${k}`}
            className={`h-11 rounded-xl text-sm font-semibold border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8A3D] ${when === k ? "bg-[#FF4D00] border-[#FF4D00] text-white" : "border-white/15 text-zinc-300"}`}>{l}</button>
        ))}
      </div>
      {when === "outro" && (
        <div className="space-y-1"><Label htmlFor="express-time" className="text-zinc-300 text-sm">Horário de início</Label>
          <Input id="express-time" data-testid="express-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className={inp} aria-invalid={!!err} aria-describedby="express-time-msg" /></div>
      )}
      <p id="express-time-msg" role={err ? "alert" : undefined} className={`text-xs ${err ? "text-rose-300" : "text-zinc-400"}`} data-testid="express-when-summary">
        {err || `Início ${date === todayISO() ? "hoje" : "amanhã"} às ${time}.`}
      </p>
    </fieldset>
  );
}

function DurationStepper({ value, onChange }) {
  return (
    <div className="space-y-1.5">
      <span id="express-duration-label" className="text-zinc-300 text-sm">Duração do show</span>
      <div role="group" aria-labelledby="express-duration-label" className="flex items-center justify-between h-11 rounded-xl border border-white/15 bg-[#09090B] px-1">
        <button type="button" aria-label="Diminuir duração" disabled={value <= 1} onClick={() => onChange(value - 1)} className={stepBtn + " h-9 w-11"} data-testid="express-duration-dec">−</button>
        <span aria-live="polite" className="text-sm font-semibold" data-testid="express-duration">{value} hora{value > 1 ? "s" : ""}</span>
        <button type="button" aria-label="Aumentar duração" disabled={value >= 12} onClick={() => onChange(value + 1)} className={stepBtn + " h-9 w-11"} data-testid="express-duration-inc">+</button>
      </div>
    </div>
  );
}

const stepBtn = "h-11 w-11 rounded-lg bg-white/10 text-lg font-bold text-zinc-100 hover:bg-white/15 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8A3D]";

function InstrumentCard({ inst, qty, onInc, onDec }) {
  const active = qty > 0;
  return (
    <div role="group" aria-label={`${inst}: ${qty} músico${qty === 1 ? "" : "s"}`} data-testid={`express-inst-${inst}`}
      className={`min-h-[96px] rounded-xl border p-2 flex flex-col items-center justify-center gap-1.5 transition-colors ${active ? "border-[#FF4D00]/60 bg-[#FF4D00]/10" : "border-white/10 bg-[#09090B]"}`}>
      <span className="text-sm font-semibold text-center leading-tight">{inst}</span>
      <div className="flex items-center gap-1">
        <button type="button" aria-label={`Remover um músico de ${inst}`} onClick={onDec} disabled={!active} className={stepBtn} data-testid={`express-inst-dec-${inst}`}>−</button>
        <span aria-hidden="true" className={`w-6 text-center font-bold ${active ? "text-[#FF8A3D]" : "text-zinc-400"}`} data-testid={`express-inst-qty-${inst}`}>{qty}</span>
        <button type="button" aria-label={`Adicionar um músico de ${inst}`} onClick={onInc} className={stepBtn} data-testid={`express-inst-inc-${inst}`}>+</button>
      </div>
    </div>
  );
}

function RadiusPicker({ radius, setRadius, labelledBy }) {
  const opts = [5, 10, 20];
  const onKey = (e) => {
    const i = opts.indexOf(radius);
    if (["ArrowRight", "ArrowDown"].includes(e.key)) { e.preventDefault(); setRadius(opts[(i + 1) % 3]); }
    if (["ArrowLeft", "ArrowUp"].includes(e.key)) { e.preventDefault(); setRadius(opts[(i + 2) % 3]); }
  };
  return (
    <div role="radiogroup" aria-labelledby={labelledBy} className="flex gap-2" onKeyDown={onKey}>
      {opts.map((r) => (
        <button key={r} type="button" role="radio" aria-checked={radius === r} tabIndex={radius === r ? 0 : -1} data-testid={`express-radius-${r}`} onClick={() => setRadius(r)}
          className={`flex-1 h-11 rounded-xl text-sm font-semibold border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8A3D] ${radius === r ? "border-[#FF4D00] bg-[#FF4D00]/15 text-[#FFB27A]" : "border-white/15 text-zinc-300"}`}>{r} km</button>
      ))}
    </div>
  );
}

function PreviewMap({ center, musicians, radius, setRadius, nearby, onDuty, loading, summary, hasLoc }) {
  return (
    <section aria-labelledby="express-preview-title" className="space-y-2" data-testid="express-preview">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 id="express-preview-title" className="font-head font-bold flex items-center gap-2"><Users className="h-5 w-5 text-[#FF8A3D]" aria-hidden="true" />Músicos no raio</h3>
        <div className="w-48"><RadiusPicker radius={radius} setRadius={setRadius} labelledBy="express-preview-title" /></div>
      </div>
      <div className="relative">
        <MapPlantao center={center} musicians={musicians} radiusKm={radius} describedBy="express-preview-summary" label={`Mapa com músicos aproximados num raio de ${radius} km`} />
        <div className="absolute top-2 left-2 z-[500] flex flex-wrap gap-1.5 pointer-events-none" data-testid="express-map-footer">
          <span className="rounded-full bg-black/75 backdrop-blur px-3 py-1.5 text-xs text-white" data-testid="express-nearby-count"><b>{nearby}</b> no raio</span>
          <span className="rounded-full bg-black/75 backdrop-blur px-3 py-1.5 text-xs text-[#FFB27A]" data-testid="express-onduty-count"><b>{onDuty}</b> em Plantão</span>
        </div>
        {loading && <div className="absolute top-2 right-2 z-[500] bg-black/70 rounded-full p-1.5"><Loader2 className="h-4 w-4 animate-spin text-[#FF4D00]" aria-hidden="true" /></div>}
      </div>
      <p id="express-preview-summary" role="status" aria-live="polite" className="text-xs text-zinc-300" data-testid="express-preview-summary">
        {summary}{!hasLoc && " Defina o local na próxima etapa para uma prévia precisa."} Posições aproximadas para proteger a privacidade dos músicos.
      </p>
      <MapLegend items={LEGEND_PRE} />
      <MusicianList musicians={musicians.filter((m) => m.distance_km != null && m.distance_km <= 20)} radiusKm={radius} />
    </section>
  );
}

function DetailRow({ icon, label, children }) {
  return (
    <div className="flex items-center gap-3 py-1">
      <div aria-hidden="true" className="h-9 w-9 rounded-xl bg-white/5 flex items-center justify-center text-muted-foreground shrink-0">{icon}</div>
      <div><div className="text-xs text-muted-foreground">{label}</div>{children}</div>
    </div>
  );
}

function OngoingCalls({ user }) {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [calls, setCalls] = useState(null);
  const [failed, setFailed] = useState(false);
  const [policy, setPolicy] = useState([]);
  const load = useCallback(() => { api.get("/express/mine").then((r) => { setCalls(r.data); setFailed(false); }).catch(() => { setFailed(true); setCalls((c) => c || []); }); }, []);
  const live = useLiveRefresh(load, { slow: 20000, fast: 4000 });
  useEffect(() => { api.get("/express/settings").then((r) => setPolicy(r.data.policy || [])).catch(() => {}); }, []);

  if (calls === null) return <div className="flex justify-center py-16" role="status"><Loader2 className="h-6 w-6 animate-spin text-[#FF4D00]" aria-hidden="true" /><span className="sr-only">Carregando chamadas…</span></div>;
  const active = calls.filter((c) => !["cancelado", "concluido"].includes(c.status));
  const featured = active.find((c) => c.id === sp.get("call")) || active.find((c) => c.searching) || active.find((c) => c.confirmed > 0) || active[0];
  const pick = (id) => { setSp({ tab: "andamento", call: id }); window.scrollTo({ top: 0, behavior: "smooth" }); };

  if (!calls.length) return (
    <div className="text-center py-16" data-testid="express-ongoing-empty">
      <Activity className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
      <h2 className="font-head text-lg font-bold">Nenhuma chamada em andamento</h2>
      <p className="text-muted-foreground mt-1 text-sm">Publique uma chamada express na aba "Criar chamada" para acompanhá-la aqui ao vivo.</p>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="express-ongoing">
      {featured && <LiveSearch c={featured} user={user} live={live} failed={failed} policy={policy} reload={load} />}
      <div className="grid md:grid-cols-2 gap-3">
        {active.filter((c) => c.id !== featured?.id).map((c) => <OngoingCard key={c.id} c={c} onOpen={() => pick(c.id)} />)}
      </div>
      {active.length === 0 && (
        <p className="text-sm text-zinc-300" data-testid="express-ended-hint">Chamadas encerradas ficam disponíveis em <button type="button" onClick={() => navigate("/relatorios")} className="underline text-[#FF8A3D] min-h-[44px]">Relatórios</button>.</p>
      )}
    </div>
  );
}

function LiveSearchMap({ c, user }) {
  const ms = c.musicians || [];
  const n = (s) => ms.filter((m) => m.status === s).length;
  const center = c.coords?.lat != null ? c.coords : user?.lat ? { lat: user.lat, lng: user.lng } : null;
  const radius = c.radius_km || 5;
  const title = c.name.replace("⚡ Em cima da Hora — ", "");
  const summary = c.open === 0 ? `Todas as vagas preenchidas. ${n("aceitou")} músico(s) aceitaram.`
    : `${c.searching ? "Buscando" : "Busca pausada"} no raio de ${radius} km. ${n("aguardando")} aguardando resposta, ${n("aceitou")} aceitaram, ${n("recusou") + n("expirou")} recusaram ou não responderam.`;
  return (
    <section aria-labelledby="express-live-title" className="space-y-2" data-testid="express-live-map">
      <h2 id="express-live-title" className="font-head font-bold flex items-center gap-2 flex-wrap">
        <span className="text-[#FF8A3D]">⚡ {title}</span><span className="text-sm text-zinc-300 font-normal">· {c.start_time}</span>
      </h2>
      <div className="relative">
        <MapPlantao center={center} musicians={ms} radiusKm={radius} describedBy="express-live-summary" label={`Mapa da busca ao vivo, raio atual de ${radius} km`} />
        <div className="absolute top-2 left-2 z-[500] rounded-full bg-black/75 backdrop-blur px-3 py-1.5 text-xs text-white pointer-events-none inline-flex items-center gap-1.5" data-testid="express-live-radius">
          {c.searching && <span aria-hidden="true" className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-[#FF4D00] opacity-60 animate-ping" /><span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF4D00]" /></span>}
          Raio atual <b>{radius} km</b>
        </div>
      </div>
      <p id="express-live-summary" role="status" aria-live="polite" aria-atomic="true" className="text-sm text-zinc-200" data-testid="express-live-summary">{summary}</p>
      <div className="grid grid-cols-3 gap-2 text-center" aria-hidden="true">
        {[["Aguardando", n("aguardando"), "#FBBF24"], ["Aceitaram", n("aceitou"), "#34D399"], ["Recusaram", n("recusou") + n("expirou"), "#A1A1AA"]].map(([l, v, col]) => (
          <div key={l} className="rounded-xl bg-black/30 border border-white/5 py-2"><div className="font-head text-lg font-bold" style={{ color: col }}>{v}</div><div className="text-xs text-zinc-400">{l}</div></div>
        ))}
      </div>
      <MapLegend items={LEGEND_LIVE} />
      <MusicianList musicians={ms} testid="express-live-list" />
    </section>
  );
}

function countdown(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "a qualquer momento";
  const min = Math.floor(ms / 60000), sec = Math.floor((ms % 60000) / 1000);
  return `em ${min}:${String(sec).padStart(2, "0")}`;
}

function OngoingCard({ c, muted, onOpen }) {
  const [, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 1000); return () => clearInterval(t); }, []);
  const protectedPay = c.payment_mode === "protected";
  const filledPct = c.slots ? Math.round((c.filled / c.slots) * 100) : 0;
  return (
    <div data-testid={`express-call-${c.id}`} className={`rounded-2xl border p-4 space-y-3 ${muted ? "border-white/5 bg-[#0D0D11] opacity-70" : "border-[#FF4D00]/25 bg-[#121217]"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-head font-bold flex items-center gap-2 flex-wrap">
            <span className="truncate">{c.name.replace("⚡ Em cima da Hora — ", "")}</span>
            {protectedPay && <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 inline-flex items-center gap-0.5"><ShieldCheck className="h-2.5 w-2.5" />Protegido</span>}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">{c.start_time}–{c.end_time} · {c.roles.map((r) => `${r.instrument} ${r.filled}/${r.quantity}`).join(" · ")}</div>
        </div>
        <span className="text-xs px-2 py-1 rounded-full border border-white/10 text-muted-foreground shrink-0 capitalize">{(c.phase || c.status || "").toLowerCase()}</span>
      </div>

      <div className="h-2 rounded-full bg-white/10 overflow-hidden" role="progressbar" aria-label="Vagas preenchidas" aria-valuemin={0} aria-valuemax={c.slots} aria-valuenow={c.filled} aria-valuetext={`${c.filled} de ${c.slots} vagas preenchidas`}><div className="h-full bg-[#FF4D00]" style={{ width: `${filledPct}%` }} /></div>

      {c.searching && (
        <div className="flex items-center gap-1.5 text-xs text-[#FF4D00]" data-testid={`express-searching-${c.id}`}>
          <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-[#FF4D00] opacity-60 animate-ping" /><span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF4D00]" /></span>
          Procurando músicos disponíveis agora…
        </div>
      )}

      <div className="space-y-1.5" data-testid={`express-roles-${c.id}`}>
        {c.roles.map((r, i) => {
          const full = r.filled >= r.quantity;
          return (
            <div key={i} className="flex items-center justify-between text-sm" data-testid={`express-role-${c.id}-${i}`}>
              <span className="flex items-center gap-1.5">{r.instrument}
                {r.invited > 0 && !full && <span className="text-xs text-amber-400">· {r.invited} convidado(s)</span>}
                {r.declined > 0 && <span className="text-xs text-muted-foreground">· {r.declined} recusou/expirou</span>}
              </span>
              <span className={`font-semibold ${full ? "text-emerald-400" : "text-foreground"}`}>{r.filled}/{r.quantity}{full ? " ✓" : ""}</span>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-4 gap-2 text-center">
        {[["Acionados", c.notified], ["Avaliando", c.evaluating], ["Convidados", c.invited], ["Confirmados", c.confirmed]].map(([l, v], i) => (
          <div key={l} className="rounded-xl bg-black/20 py-2" data-testid={`express-stat-${i}`}>
            <div className={`font-head text-lg font-bold ${l === "Avaliando" && v > 0 ? "text-[#FF4D00]" : ""}`}>{v}</div>
            <div className="text-xs text-muted-foreground">{l}</div>
          </div>
        ))}
      </div>

      {!muted && (
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground inline-flex items-center gap-1.5">
            <Radar className="h-3.5 w-3.5 text-[#FF4D00]" />Raio atual <b className="text-foreground">{c.radius_km || 5} km</b>
            {!c.fanout_done && c.open > 0 && c.next_expand_at && <span className="text-muted-foreground">· amplia {countdown(c.next_expand_at)}</span>}
            {c.fanout_done && c.open > 0 && <span className="text-amber-400">· raio máximo</span>}
            {c.open === 0 && <span className="text-emerald-400">· vagas preenchidas 🎉</span>}
          </span>
        </div>
      )}

      <Button data-testid={`express-open-${c.id}`} onClick={onOpen} aria-label={`Abrir chamada ${c.name.replace("⚡ Em cima da Hora — ", "")}`} className="w-full h-11 rounded-xl bg-[#FF4D00] hover:bg-[#E64500] text-white gap-1.5"><ArrowRight className="h-4 w-4" />{muted ? "Abrir evento" : "Acompanhar ao vivo"}</Button>
    </div>
  );
}
